import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icon/icon.component';
import { ButtonComponent } from '../../forms/button/button.component';
import { SegmentedComponent, SegmentedConfig } from '../segmented/segmented.component';
import { StepperStepDirective } from './stepper-step.directive';
import { StepperMode, StepperStep } from './stepper.model';

/**
 * Generic stepped-modal shell: a scrim, a titled card, a rail of steps with completion state, and a footer
 * of Back / Next / Save — in two layouts the header toggles between (one step at a time, or every step in
 * one scroll). It is presentational: the host supplies the step bodies as templates, owns each step's
 * `status`, and gates forward movement by handling `(next)`. Rail jumps and Back move freely.
 */
@Component({
  selector: 'app-stepper',
  standalone: true,
  imports: [CommonModule, IconComponent, ButtonComponent, SegmentedComponent],
  templateUrl: './stepper.component.html',
  styleUrl: './stepper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepperComponent implements AfterContentInit {
  @Input() title = '';
  @Input() steps: StepperStep[] = [];
  @Input() activeStepId = '';
  @Output() activeStepIdChange = new EventEmitter<string>();

  @Input() mode: StepperMode = 'step';
  @Output() modeChange = new EventEmitter<StepperMode>();

  @Input() showLayoutToggle = true;
  @Input() busy = false;
  @Input() saveLabel = 'Save';
  @Input() cancelLabel = 'Cancel';
  @Input() hint: string | null = null;

  @Output() next = new EventEmitter<string>();
  @Output() back = new EventEmitter<string>();
  @Output() save = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() stepSelect = new EventEmitter<string>();

  @ContentChildren(StepperStepDirective)
  private readonly stepTemplates!: QueryList<StepperStepDirective>;
  @ViewChild('body') private readonly bodyRef?: ElementRef<HTMLElement>;

  private readonly cdr = inject(ChangeDetectorRef);

  readonly layoutOptions: SegmentedConfig = {
    size: 'sm',
    options: [
      { label: 'Step by step', value: 'step' },
      { label: 'All details', value: 'all' },
    ],
  };

  ngAfterContentInit(): void {
    if (!this.activeStepId && this.steps.length) this.setActive(this.steps[0].id);
  }

  /** Steps shown in the all-details scroll — everything but the review. */
  get scrollSteps(): StepperStep[] {
    return this.steps.filter((s) => !s.isReview);
  }

  get railSteps(): StepperStep[] {
    return this.mode === 'all' ? this.scrollSteps : this.steps;
  }

  get activeStep(): StepperStep | undefined {
    return this.steps.find((s) => s.id === this.activeStepId) ?? this.steps[0];
  }

  private get activeIndex(): number {
    return this.steps.findIndex((s) => s.id === this.activeStep?.id);
  }

  get isFirst(): boolean {
    return this.activeIndex <= 0;
  }

  get isReviewActive(): boolean {
    return !!this.activeStep?.isReview;
  }

  get nextLabel(): string {
    const nextStep = this.steps[this.activeIndex + 1];
    return nextStep ? `Next: ${nextStep.label}` : 'Next';
  }

  get completedCount(): number {
    return this.scrollSteps.filter((s) => s.status === 'done').length;
  }

  get totalCount(): number {
    return this.scrollSteps.length;
  }

  get progressPercent(): number {
    return this.totalCount ? (this.completedCount / this.totalCount) * 100 : 0;
  }

  get stepEyebrow(): string {
    const index = this.activeIndex;
    return index >= 0 ? `Step ${index + 1} of ${this.steps.length}` : '';
  }

  templateFor(id: string): TemplateRef<unknown> | null {
    return this.stepTemplates?.find((t) => t.id === id)?.template ?? null;
  }

  stepNumber(step: StepperStep): number {
    return this.steps.findIndex((s) => s.id === step.id) + 1;
  }

  /** A completed step shows a tick once the reader has moved past it; the active one keeps its number. */
  showTick(step: StepperStep): boolean {
    return !step.isReview && step.status === 'done' && step.id !== this.activeStep?.id;
  }

  showError(step: StepperStep): boolean {
    return !step.isReview && step.status === 'error';
  }

  selectStep(step: StepperStep): void {
    if (this.mode === 'all') {
      this.scrollToStep(step.id);
      return;
    }
    this.setActive(step.id);
    this.stepSelect.emit(step.id);
  }

  onNext(): void {
    this.next.emit(this.activeStep?.id ?? '');
  }

  onBack(): void {
    const prev = this.steps[this.activeIndex - 1];
    if (prev) this.setActive(prev.id);
    this.back.emit(prev?.id ?? '');
  }

  onSave(): void {
    this.save.emit();
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  onModeChange(mode: string): void {
    const next = mode as StepperMode;
    if (next === this.mode) return;
    this.mode = next;
    this.modeChange.emit(next);
    if (next === 'all') {
      const target =
        this.scrollSteps.find((s) => s.id === this.activeStepId) ?? this.scrollSteps[0];
      if (target) setTimeout(() => this.scrollToStep(target.id, true));
    }
  }

  onBodyScroll(): void {
    if (this.mode !== 'all') return;
    const body = this.bodyRef?.nativeElement;
    if (!body) return;
    let current = this.scrollSteps[0]?.id;
    body.querySelectorAll<HTMLElement>('[data-sec]').forEach((el) => {
      if (el.offsetTop - 60 <= body.scrollTop) current = el.dataset['sec'] ?? current;
    });
    if (body.scrollTop + body.clientHeight >= body.scrollHeight - 4) {
      current = this.scrollSteps[this.scrollSteps.length - 1]?.id ?? current;
    }
    if (current && current !== this.activeStepId) {
      this.activeStepId = current;
      this.activeStepIdChange.emit(current);
      this.cdr.markForCheck();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.onCancel();
  }

  private setActive(id: string): void {
    if (id === this.activeStepId) return;
    this.activeStepId = id;
    this.activeStepIdChange.emit(id);
    const body = this.bodyRef?.nativeElement;
    if (body) body.scrollTop = 0;
  }

  private scrollToStep(id: string, keepActive = false): void {
    const body = this.bodyRef?.nativeElement;
    const target = body?.querySelector<HTMLElement>(`#sec-${id}`);
    if (body && target) body.scrollTop = target.offsetTop - 4;
    if (!keepActive && id !== this.activeStepId) {
      this.activeStepId = id;
      this.activeStepIdChange.emit(id);
    }
  }
}
